import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, DollarSign, Target, Brain, Lock, Crown, Zap, Calendar, PieChart as PieChartIcon, Activity, AlertCircle, CheckCircle, LogOut, Plus, Trash2, Edit2, User, Moon, Sun, BookOpen, LifeBuoy, Upload, Download, CreditCard, TrendingDown, ChevronDown, Wallet, BarChart3, Settings, Menu, X, ChevronRight, Receipt } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useCountry } from './CountryContext';
import { useTheme } from './ThemeContext';
import CryptoTracker from './CryptoTracker';
import AccountTypeManager from './AccountTypeManager';
import PortfolioAnalysis from './PortfolioAnalysis';
import RetirementPlanner from './components/RetirementPlanner';
import EnhancedAIAnalysis from './components/EnhancedAIAnalysis';
import AIInsights from './components/AIInsights';
import UserProfile from './components/UserProfile';
import HelpAndLearn from './HelpAndLearn';
import SupportForm from './components/SupportForm';
import CSVImportModal from './components/CSVImportModal';
import LiabilitiesManager from './components/LiabilitiesManager';
import FinancialGoals from './components/FinancialGoals';
import NetWorthDashboard from './components/NetWorthDashboard';
import DataCleanupPanel from './components/DataCleanupPanel';
import TopHoldings from './components/TopHoldings';
import WealthTrackingDashboard from './components/WealthTracking/WealthTrackingDashboard';
import { VanguardPortfolioDashboard } from './components/vanguard';
import { getAllSnapshots } from './utils/wealthTracking/snapshotManager';
import BudgetOverview from './components/budgeting/BudgetOverview';
import { SimplePortfolioSummary } from './components/SimplePortfolioSummary';
import { useSimplePortfolioValue } from './hooks/useSimplePortfolioValue';
import { usePortfolioPrices } from './hooks/usePricing';
import MonthlyBudget from './components/budgeting/MonthlyBudget';
import Transactions from './components/budgeting/Transactions';
import Trends from './components/budgeting/Trends';
import {
  generateInvestmentsTemplate,
  downloadCSV,
  validateInvestmentsCSV,
  convertCSVToInvestments,
  exportInvestmentsToCSV
} from './utils/csvUtils';
import { collection, addDoc, deleteDoc, doc, getDocs, updateDoc, writeBatch } from 'firebase/firestore';
import { migrateInvestmentsArray, checkMigrationNeeded } from './utils/currencyMigration';
import { useExchangeRates } from './hooks/useExchangeRates';
import { calculatePortfolioValue } from './utils/multiCurrencyUtils';
import { getCurrencyCode } from './countryConfig';
import { db } from './firebase';
import { formatCurrency, getCurrencySymbol, getCurrencySymbolByCode } from './countryConfig';
import {
  calculateVolatility,
  calculateSharpeRatio,
  calculateExpectedReturn,
  generateScenarioProjections,
  calculateDiversificationScore,
  generateAICommentary,
  generateCountrySpecificRecommendations,
  aggregatePortfolioHoldings
} from './utils/ai/enhancedAnalysis';
import {
  groupInvestmentsByAccount,
  groupInvestmentsByCategory,
  calculateAccountBalance,
  getAccountsWithBalances
} from './utils/accountUtils';
import { getPaymentLinkUrl } from './stripeConfig';
import {
  addToPosition,
  calculatePositionValue,
  createPurchaseRecord,
  getUnitLabel,
  formatQuantity
} from './services/positionService';

const InvestmentRetirementApp = ({ onNavigateToLanding }) => {
  const { currentUser, userProfile, updateUserProfile, logout } = useAuth();
  const { selectedCountry, currentCountry } = useCountry();
  const { theme, toggleTheme, isDark } = useTheme();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // ⭐ MULTI-CURRENCY: Exchange rates hook
  const { rates: exchangeRates, loading: ratesLoading } = useExchangeRates();

  console.log('InvestmentRetirementApp - Selected Country:', selectedCountry);
  console.log('InvestmentRetirementApp - User Profile Country:', userProfile?.country);
  console.log('InvestmentRetirementApp - Currency Symbol:', currencySymbol);
  console.log('InvestmentRetirementApp - Exchange Rates Loaded:', !ratesLoading);

  // Original state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeBillingCycle, setUpgradeBillingCycle] = useState('monthly'); // For upgrade modal
  const [upgrading, setUpgrading] = useState(false); // Loading state for upgrade
  const [sidebarOpen, setSidebarOpen] = useState(false); // For mobile sidebar toggle
  const [expandedGroups, setExpandedGroups] = useState(['wealth', 'investments', 'planning', 'more']); // Track which groups are expanded
  const [showAddInvestment, setShowAddInvestment] = useState(false);
  const [showImportInvestmentsModal, setShowImportInvestmentsModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portfolioView, setPortfolioView] = useState('all'); // 'all', 'byAccount', 'byCategory'
  const [dashboardView, setDashboardView] = useState('vanguard'); // 'vanguard' or 'classic' - default to new Vanguard style
  
  // ⭐ portfolioData is now derived from portfolioMetrics (see useMemo around line 165)
  // No longer using useState - calculated using market values from pricing hook

  const [investments, setInvestments] = useState([]);
  const [wealthSnapshots, setWealthSnapshots] = useState([]);

  // Track if initial data has been loaded from userProfile
  // Prevents useEffect from overwriting local changes after saves
  const initialDataLoadedRef = useRef(false);

  const [retirementGoals, setRetirementGoals] = useState({
    currentAge: 30,
    retirementAge: 65,
    currentSavings: 0,
    monthlyContribution: 0,
    desiredIncome: 50000,
    expectedReturn: 7
  });

  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const [newInvestment, setNewInvestment] = useState({
    name: '',
    ticker: '',
    isin: '', // For UK funds without exchange ticker (e.g., GB00B0CNH163)
    amount: '', // Legacy - will be calculated from quantity × pricePerUnit
    quantity: '', // Number of shares/coins/units
    pricePerUnit: '', // Purchase price per unit
    priceInPence: false, // For UK stocks quoted in pence
    return: '',
    risk: 'Medium',
    category: 'Stocks',
    accountId: '', // Link investment to account
    accountName: '', // Helper field for display
    purchaseDate: '', // Purchase date
    currency: selectedCountry === 'US' ? 'USD' : 'GBP' // MULTI-CURRENCY: Native currency of the asset
  });

  // Add to Position modal state
  const [showAddToPosition, setShowAddToPosition] = useState(false);
  const [addToPositionTarget, setAddToPositionTarget] = useState(null);
  const [addToPositionData, setAddToPositionData] = useState({
    quantity: '',
    pricePerUnit: '',
    priceInPence: false, // For UK stocks quoted in pence
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Edit investment state
  const [showEditInvestment, setShowEditInvestment] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);

  // NEW STATE FOR NEW FEATURES
  const [accounts, setAccounts] = useState([]);
  const [cryptocurrencies, setCryptocurrencies] = useState([]);

  // ⭐ PRICING: Portfolio value hook (Phase 2) - Must be after investments state is defined
  const displayCurrency = getCurrencyCode(selectedCountry);
  const portfolioMetrics = useSimplePortfolioValue(investments, displayCurrency);

  // ⭐ LIVE PRICING: Get price map for account balance calculations
  const { prices: priceMap } = usePortfolioPrices(investments);

  // ⭐ CHARTS: Derive portfolio allocation data from portfolio metrics (using market values)
  const portfolioData = useMemo(() => {
    const categories = {
      'Stocks': { value: 0, color: '#3b82f6' },        // Blue
      'Bonds': { value: 0, color: '#8b5cf6' },         // Purple
      'Real Estate': { value: 0, color: '#10b981' },   // Emerald green
      'Commodities': { value: 0, color: '#facc15' },   // Bright yellow
      'Cryptocurrency': { value: 0, color: '#ec4899' }, // Pink
      'Cash': { value: 0, color: '#fb923c' }           // Coral orange
    };

    // Aggregate by category using market values (not cost basis)
    portfolioMetrics.investmentDetails.forEach(inv => {
      if (categories[inv.category]) {
        categories[inv.category].value += inv.marketValue || inv.costBasis || 0;
      }
    });

    // Calculate total for percentages
    const total = Object.values(categories).reduce((sum, cat) => sum + cat.value, 0);

    // Convert to array format for charts
    return Object.entries(categories).map(([name, data]) => ({
      name,
      value: data.value,
      allocation: total > 0 ? ((data.value / total) * 100).toFixed(1) : 0,
      color: data.color
    }));
  }, [portfolioMetrics.investmentDetails]);

  // NEW: Load accounts and cryptocurrencies from Firebase (wrapped in useCallback)
  const loadAccountsAndCrypto = useCallback(async () => {
    if (!currentUser) return;

    try {
      // Load accounts
      const accountsRef = collection(db, 'users', currentUser.uid, 'accounts');
      const accountsSnapshot = await getDocs(accountsRef);
      const accountsData = accountsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAccounts(accountsData);

      // Load cryptocurrencies
      const cryptoRef = collection(db, 'users', currentUser.uid, 'cryptocurrencies');
      const cryptoSnapshot = await getDocs(cryptoRef);
      const cryptoData = cryptoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCryptocurrencies(cryptoData);
    } catch (error) {
      console.error('Error loading accounts and crypto:', error);
    }
  }, [currentUser]);

  // Load user data from Firebase ONLY on initial mount or when user changes
  // ⭐ FIX: Don't overwrite local state changes when userProfile updates after save
  useEffect(() => {
    if (userProfile && !initialDataLoadedRef.current) {
      // ⭐ REMOVED: Do NOT load portfolioData from Firebase cache
      // portfolioData is DERIVED state that must be calculated from investments
      // Loading cached portfolioData causes stale values (without currency conversions)
      // if (userProfile.portfolioData) {
      //   setPortfolioData(userProfile.portfolioData);
      // }
      if (userProfile.investments) {
        setInvestments(userProfile.investments);
      }
      if (userProfile.retirementGoals) {
        setRetirementGoals(userProfile.retirementGoals);
      }
      // Mark initial data as loaded to prevent overwriting local changes
      initialDataLoadedRef.current = true;
    }
  }, [userProfile]);

  // Reset initial data loaded flag when user changes (logout/login)
  useEffect(() => {
    if (!currentUser) {
      initialDataLoadedRef.current = false;
    }
  }, [currentUser]);

  // Load accounts and cryptocurrencies when user logs in
  useEffect(() => {
    if (currentUser) {
      loadAccountsAndCrypto();
    }
  }, [currentUser, loadAccountsAndCrypto]);

  // Load wealth snapshots for Vanguard dashboard
  useEffect(() => {
    const loadSnapshots = async () => {
      if (!currentUser) return;
      try {
        const snapshots = await getAllSnapshots(currentUser.uid);
        setWealthSnapshots(snapshots);
      } catch (error) {
        console.error('Error loading wealth snapshots:', error);
      }
    };
    loadSnapshots();
  }, [currentUser]);

  // ⭐ MULTI-CURRENCY: Auto-migrate investments to include currency field
  useEffect(() => {
    if (investments.length > 0 && checkMigrationNeeded(investments)) {
      console.log('📊 Migrating investments to include currency field...');
      const defaultCurrency = getCurrencyCode(selectedCountry);
      const migratedInvestments = migrateInvestmentsArray(investments, defaultCurrency);

      // Update state with migrated investments
      setInvestments(migratedInvestments);

      // Save to Firebase
      if (currentUser) {
        updateUserProfile(currentUser.uid, { investments: migratedInvestments })
          .then(() => {
            console.log('✅ Investments migrated successfully to', defaultCurrency);
          })
          .catch(err => {
            console.error('❌ Error saving migrated investments:', err);
          });
      }
    }
  }, [investments, selectedCountry, currentUser, updateUserProfile]);

  // Load cached built-in analysis from localStorage on mount
  useEffect(() => {
    if (!currentUser) return;

    const cacheKey = `builtin_analysis_${currentUser.uid}`;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const analysisData = JSON.parse(cached);
        const ageInHours = (Date.now() - (analysisData.timestamp || 0)) / (1000 * 60 * 60);

        // Load cache if less than 24 hours old
        if (ageInHours < 24) {
          console.log('[Built-in Analysis] Loading cached analysis from', new Date(analysisData.timestamp).toLocaleString());
          setAiAnalysis(analysisData);
        } else {
          console.log('[Built-in Analysis] Cached analysis expired, clearing...');
          localStorage.removeItem(cacheKey);
        }
      } catch (err) {
        console.error('[Built-in Analysis] Failed to load cached analysis:', err);
        localStorage.removeItem(cacheKey);
      }
    }
  }, [currentUser]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [activeTab]);

  // Save data to Firebase
  const saveToFirebase = useCallback(async (data) => {
    if (!currentUser) return;

    setSaving(true);
    try {
      await updateUserProfile(currentUser.uid, data);
    } catch (error) {
      console.error('Error saving to Firebase:', error);
    } finally {
      setSaving(false);
    }
  }, [currentUser, updateUserProfile]);

  // Update portfolio data (derived state - NOT saved to Firebase)
  // ⭐ portfolioData is calculated from investments, so we don't cache it
  const updatePortfolioData = useCallback((newData) => {
    console.log('[Portfolio] updatePortfolioData called with:', newData);
    setPortfolioData(newData);
    console.log('[Portfolio] portfolioData state updated');
    // ⭐ REMOVED: Don't save portfolioData to Firebase - it's derived from investments
    // saveToFirebase({ portfolioData: newData });
  }, []);

  // Recalculate portfolio based on investments, accounts, and crypto
  // ⭐ MULTI-CURRENCY: Now converts all amounts to display currency
  // ⭐ FIX: Wrapped in useCallback to prevent stale closure of exchangeRates
  const recalculatePortfolio = useCallback((investmentsList, accountsList = accounts, cryptoList = cryptocurrencies) => {
    // 🐛 DEBUG: Log exchange rates status
    console.log('[Portfolio] Recalculating with exchange rates:', exchangeRates ? 'Available' : 'NOT AVAILABLE', exchangeRates);

    const categories = {
      'Stocks': { value: 0, color: '#3b82f6' },        // Blue
      'Bonds': { value: 0, color: '#8b5cf6' },          // Purple
      'Real Estate': { value: 0, color: '#10b981' },    // Emerald green
      'Commodities': { value: 0, color: '#facc15' },    // Bright yellow (gold)
      'Cryptocurrency': { value: 0, color: '#ec4899' }, // Pink/magenta - Support crypto in investments
      'Cash': { value: 0, color: '#fb923c' }            // Coral orange
    };

    // ⭐ DEFENSIVE VALIDATION: Filter out orphaned investments
    // Only validate if accounts have been loaded (to avoid false positives during initial load)
    const validInvestments = [];
    const orphanedInvestments = [];

    if (accountsList.length === 0) {
      // Accounts not loaded yet - skip validation and include all investments
      validInvestments.push(...investmentsList);
    } else {
      // Accounts are loaded - perform validation
      const validAccountIds = new Set(accountsList.map(acc => acc.id));

      investmentsList.forEach(inv => {
        // If investment has an accountId, verify it exists
        if (inv.accountId && !validAccountIds.has(inv.accountId)) {
          orphanedInvestments.push(inv);
          console.warn(
            `[Portfolio Calculation] Skipping orphaned investment: ${inv.name} (${inv.id}) - ` +
            `accountId "${inv.accountId}" does not exist`
          );
        } else {
          validInvestments.push(inv);
        }
      });

      // Alert user if orphaned investments were found (only when accounts are loaded)
      if (orphanedInvestments.length > 0) {
        const orphanedValue = orphanedInvestments.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
        console.error(
          `[Portfolio Calculation] ⚠️  ORPHANED DATA DETECTED!\n` +
          `Found ${orphanedInvestments.length} orphaned investment(s) worth ${formatCurrency(orphanedValue, selectedCountry)}\n` +
          `These investments are excluded from calculations.\n` +
          `Use the Data Cleanup tool to fix this issue.`
        );
      }
    }

    // ⭐ MULTI-CURRENCY: Get display currency and exchange rates
    const displayCurrency = getCurrencyCode(selectedCountry);

    // Add investments by category (using validated list)
    // ⭐ MULTI-CURRENCY: Convert each investment to display currency
    validInvestments.forEach(inv => {
      if (categories[inv.category]) {
        const amount = parseFloat(inv.amount) || 0;
        const investmentCurrency = inv.currency || displayCurrency;

        // Convert to display currency if exchange rates available
        let convertedAmount = amount;
        if (exchangeRates && exchangeRates.rates && investmentCurrency !== displayCurrency) {
          try {
            const fromRate = exchangeRates.rates[investmentCurrency];
            const toRate = exchangeRates.rates[displayCurrency];
            if (fromRate && toRate) {
              convertedAmount = (amount / fromRate) * toRate;
              // 🐛 DEBUG: Log conversion
              console.log(`[Portfolio] Converting ${inv.name}: ${amount} ${investmentCurrency} → ${convertedAmount.toFixed(2)} ${displayCurrency} (rate: ${fromRate}→${toRate})`);
            } else {
              console.warn(`[Portfolio] Missing exchange rates for ${investmentCurrency} or ${displayCurrency}`, { fromRate, toRate });
            }
          } catch (error) {
            console.warn(`[Portfolio] Currency conversion failed for ${inv.name}`, error);
          }
        } else if (investmentCurrency !== displayCurrency) {
          console.warn(`[Portfolio] Exchange rates not available, treating ${amount} ${investmentCurrency} as ${displayCurrency}`);
        }

        categories[inv.category].value += convertedAmount;
      }
    });

    // ⭐ REMOVED: No longer add account balances to portfolio
    // Accounts are containers - their value is already represented by the investments they hold
    // This fixes the bug where portfolio values were being doubled

    // Add cryptocurrency values from separate crypto holdings (legacy)
    // Only add crypto that's NOT already in investments
    const cryptoInInvestments = investmentsList
      .filter(inv => inv.category === 'Cryptocurrency')
      .reduce((sum, inv) => sum + inv.amount, 0);

    cryptoList.forEach(crypto => {
      const value = (crypto.amount || 0) * (crypto.currentPrice || crypto.purchasePrice || 0);
      categories['Cryptocurrency'].value += value;
    });

    const total = Object.values(categories).reduce((sum, cat) => sum + cat.value, 0);

    const newPortfolioData = Object.entries(categories).map(([name, data]) => ({
      name,
      value: data.value,
      allocation: total > 0 ? Math.round((data.value / total) * 100) : 0,
      color: data.color
    }));

    console.log('[Portfolio] New portfolio data calculated:', newPortfolioData);
    console.log('[Portfolio] Total portfolio value:', total);
    updatePortfolioData(newPortfolioData);
  }, [exchangeRates, selectedCountry, accounts, cryptocurrencies, updatePortfolioData]);

  // Recalculate portfolio whenever accounts, crypto, investments, exchange rates, or country changes
  // ⭐ MULTI-CURRENCY: Added exchangeRates and selectedCountry to dependencies
  // ⭐ No longer needed: portfolioData is now derived via useMemo from portfolioMetrics
  // It automatically updates when investments, prices, or exchange rates change
  // useEffect(() => {
  //   if (investments.length > 0 || accounts.length > 0 || cryptocurrencies.length > 0) {
  //     recalculatePortfolio(investments, accounts, cryptocurrencies);
  //   }
  // }, [accounts, cryptocurrencies, investments, exchangeRates, selectedCountry]);

  // Helper function to sanitize investment data for Firestore
  // Firestore doesn't accept undefined values, only null
  const sanitizeForFirestore = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (Array.isArray(obj)) {
      return obj.map(item => sanitizeForFirestore(item));
    }
    if (typeof obj === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(obj)) {
        sanitized[key] = value === undefined ? null : sanitizeForFirestore(value);
      }
      return sanitized;
    }
    return obj;
  };

  // Update investments and save to Firebase
  const updateInvestments = (newInvestments) => {
    // Sanitize investments to convert undefined values to null (Firestore requirement)
    const sanitizedInvestments = sanitizeForFirestore(newInvestments);

    setInvestments(sanitizedInvestments);
    saveToFirebase({ investments: sanitizedInvestments });

    // ⭐ portfolioData now auto-updates via useMemo when investments change
    // No need to manually recalculate
  };

  // Update retirement goals and save to Firebase
  const updateRetirementGoalsData = (newGoals) => {
    setRetirementGoals(newGoals);
    saveToFirebase({ retirementGoals: newGoals });
  };

  // Add new investment (supports both quantity-based and legacy amount-based)
  const handleAddInvestment = () => {
    // Validate required fields
    if (!newInvestment.name || !newInvestment.return) {
      alert('Please fill in Investment Name and Expected Return');
      return;
    }

    // Check if using quantity-based or amount-based input
    const hasQuantity = newInvestment.quantity && parseFloat(newInvestment.quantity) > 0;
    const hasPricePerUnit = newInvestment.pricePerUnit && parseFloat(newInvestment.pricePerUnit) > 0;
    const hasAmount = newInvestment.amount && parseFloat(newInvestment.amount) > 0;

    // Require either (quantity + pricePerUnit) OR amount
    if (!hasQuantity && !hasAmount) {
      alert('Please enter either Quantity and Price Per Unit, or Total Amount');
      return;
    }

    // Calculate values based on input method
    let quantity, pricePerUnit, totalAmount;

    if (hasQuantity && hasPricePerUnit) {
      // Quantity-based input (preferred)
      quantity = parseFloat(newInvestment.quantity);
      let rawPrice = parseFloat(newInvestment.pricePerUnit);

      // Convert pence to pounds if needed (UK stocks often quoted in pence)
      if (newInvestment.priceInPence && newInvestment.currency === 'GBP') {
        pricePerUnit = rawPrice / 100; // Convert pence to pounds
      } else {
        pricePerUnit = rawPrice;
      }

      totalAmount = Math.round(quantity * pricePerUnit * 100) / 100;
    } else if (hasAmount) {
      // Legacy amount-based input
      totalAmount = parseFloat(newInvestment.amount);
      quantity = 1; // Default to 1 unit
      pricePerUnit = totalAmount;
    } else {
      alert('Please enter Price Per Unit along with Quantity');
      return;
    }

    const investment = {
      id: Date.now(),
      name: newInvestment.name,
      ticker: newInvestment.ticker || '',
      isin: newInvestment.isin || '', // For UK funds without exchange ticker
      // Quantity-based fields
      quantity: quantity,
      averageCostBasis: pricePerUnit, // Always stored in pounds/dollars (not pence)
      totalCostBasis: totalAmount,
      purchaseCount: 1,
      // Legacy field for backward compatibility
      amount: totalAmount,
      // Other fields
      return: parseFloat(newInvestment.return),
      risk: newInvestment.risk,
      category: newInvestment.category,
      accountId: newInvestment.accountId || null,
      accountName: newInvestment.accountName || 'Unassigned',
      purchaseDate: newInvestment.purchaseDate || new Date().toISOString().split('T')[0],
lastPurchaseDate: newInvestment.purchaseDate || new Date().toISOString().split('T')[0],
      currency: newInvestment.currency || (selectedCountry === 'US' ? 'USD' : 'GBP'),
      unit: getUnitLabel(newInvestment.category),
      // Enhanced tracking fields (optional)
      quantity: newInvestment.quantity ? parseFloat(newInvestment.quantity) : null,
      purchasePrice: newInvestment.pricePerUnit ? parseFloat(newInvestment.pricePerUnit) : null
    };

    updateInvestments([...investments, investment]);

    // Reset form
    setNewInvestment({
      name: '',
      ticker: '',
      isin: '',
      amount: '',
      quantity: '',
      pricePerUnit: '',
      priceInPence: false,
      return: '',
      risk: 'Medium',
      category: 'Stocks',
      accountId: '',
      accountName: '',
      purchaseDate: '',
      currency: selectedCountry === 'US' ? 'USD' : 'GBP'
    });
    setShowAddInvestment(false);
  };

  // Handle adding to an existing position (buy more shares)
  const handleAddToPosition = () => {
    if (!addToPositionTarget) {
      alert('No investment selected');
      return;
    }

    const quantity = parseFloat(addToPositionData.quantity);
    let rawPrice = parseFloat(addToPositionData.pricePerUnit);

    if (!quantity || quantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    if (!rawPrice || rawPrice <= 0) {
      alert('Please enter a valid price per unit');
      return;
    }

    // Convert pence to pounds if needed
    let pricePerUnit = rawPrice;
    if (addToPositionData.priceInPence && addToPositionTarget.currency === 'GBP') {
      pricePerUnit = rawPrice / 100;
    }

    // Calculate new position using positionService
    const updatedFields = addToPosition(addToPositionTarget, {
      quantity,
      pricePerUnit,
      date: addToPositionData.date,
      notes: addToPositionData.notes
    });

    // Update the investment with new values
    const updatedInvestment = {
      ...addToPositionTarget,
      ...updatedFields,
      unit: getUnitLabel(addToPositionTarget.category)
    };

    // Update investments array
    updateInvestments(investments.map(inv =>
      inv.id === addToPositionTarget.id ? updatedInvestment : inv
    ));

    // Reset modal state
    setShowAddToPosition(false);
    setAddToPositionTarget(null);
    setAddToPositionData({
      quantity: '',
      pricePerUnit: '',
      priceInPence: false,
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  // Open Add to Position modal
  const handleOpenAddToPosition = (investment) => {
    setAddToPositionTarget(investment);
    setAddToPositionData({
      quantity: '',
      pricePerUnit: investment.averageCostBasis || '', // Pre-fill with last price
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowAddToPosition(true);
  };

  // Delete investment
  const handleDeleteInvestment = (id) => {
    if (window.confirm('Are you sure you want to delete this investment?')) {
      updateInvestments(investments.filter(inv => inv.id !== id));
    }
  };

  // Edit investment
  const handleEditInvestmentClick = (investment) => {
    setEditingInvestment({
      ...investment,
      accountId: investment.accountId || '',
      accountName: investment.accountName || '',
      currency: investment.currency || (selectedCountry === 'US' ? 'USD' : 'GBP') // ⭐ MULTI-CURRENCY: Ensure currency field exists
    });
    setShowEditInvestment(true);
  };

  // Update investment
  const handleUpdateInvestment = () => {
    if (!editingInvestment.name || !editingInvestment.amount || !editingInvestment.return) {
      alert('Please fill in all fields');
      return;
    }

    // Build updated investment with only core fields (exclude computed fields from VanguardHoldingsTable)
    const updatedInvestment = {
      // Core identification
      id: editingInvestment.id,
      name: editingInvestment.name,
      ticker: editingInvestment.ticker || '',
      isin: editingInvestment.isin || '',
      // Financial data
      amount: parseFloat(editingInvestment.amount),
      return: parseFloat(editingInvestment.return),
      risk: editingInvestment.risk || 'Medium',
      category: editingInvestment.category || 'Stocks',
      currency: editingInvestment.currency || (selectedCountry === 'US' ? 'USD' : 'GBP'),
      // Account association
      accountId: editingInvestment.accountId || null,
      accountName: editingInvestment.accountName || 'Unassigned',
      // Quantity tracking (optional)
      quantity: editingInvestment.quantity ? parseFloat(editingInvestment.quantity) : null,
      purchasePrice: editingInvestment.purchasePrice ? parseFloat(editingInvestment.purchasePrice) : null,
      averageCostBasis: editingInvestment.averageCostBasis || null,
      totalCostBasis: editingInvestment.totalCostBasis || null,
      purchaseCount: editingInvestment.purchaseCount || 1,
      // Dates
      purchaseDate: editingInvestment.purchaseDate || null,
      lastPurchaseDate: editingInvestment.lastPurchaseDate || null,
      // Unit label
      unit: editingInvestment.unit || null
    };

    updateInvestments(investments.map(inv =>
      inv.id === updatedInvestment.id ? updatedInvestment : inv
    ));

    setEditingInvestment(null);
    setShowEditInvestment(false);
  };

  // Update investment sector/region data
  const handleUpdateInvestmentSectorRegion = (investmentId, sectorRegionData) => {
    updateInvestments(investments.map(inv => {
      if (inv.id === investmentId) {
        return {
          ...inv,
          sectorBreakdown: sectorRegionData.sectorBreakdown,
          regionBreakdown: sectorRegionData.regionBreakdown
        };
      }
      return inv;
    }));
  };

  // CSV Import/Export Handlers for Investments
  const handleDownloadInvestmentsTemplate = () => {
    const template = generateInvestmentsTemplate();
    downloadCSV(template, 'investments-import-template.csv');
  };

  const handleImportInvestmentsCSV = async (data) => {
    // Convert CSV data to investment objects
    const newInvestments = convertCSVToInvestments(data, accounts);

    // Prepare all investments with unique IDs
    const results = { success: 0, failed: 0, errors: [] };
    const investmentsToAdd = [];

    for (let i = 0; i < newInvestments.length; i++) {
      const investment = newInvestments[i];
      try {
        const investmentData = {
          ...investment,
          id: Date.now() + i // Generate unique ID
        };
        investmentsToAdd.push(investmentData);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push({ investment: investment.name, error: error.message });
      }
    }

    // Add all investments at once
    if (investmentsToAdd.length > 0) {
      updateInvestments([...investments, ...investmentsToAdd]);
    }

    return results;
  };

  const handleExportInvestmentsCSV = () => {
    const csv = exportInvestmentsToCSV(investments, accounts);
    downloadCSV(csv, 'planlens-investments-export.csv');
  };

  // NEW: Account management functions
  const handleAddAccount = async (accountData) => {
    if (!currentUser) {
      alert('Please log in to add an account');
      return;
    }

    try {
      const accountsRef = collection(db, 'users', currentUser.uid, 'accounts');
      const docRef = await addDoc(accountsRef, accountData);
      
      // Only update state after successful Firebase operation
      setAccounts(prev => [...prev, { id: docRef.id, ...accountData }]);
      console.log('Account added successfully');
    } catch (error) {
      console.error('Error adding account:', error);
      alert('Failed to add account: ' + error.message);
      throw error; // Re-throw so component can handle it
    }
  };

  const handleRemoveAccount = async (accountId) => {
    if (!currentUser) return;

    // ⭐ CASCADE DELETE: Count investments in this account
    const accountInvestments = investments.filter(inv => inv.accountId === accountId);
    const investmentCount = accountInvestments.length;

    // Enhanced confirmation dialog showing what will be deleted
    const confirmMessage = investmentCount > 0
      ? `This account has ${investmentCount} investment${investmentCount > 1 ? 's' : ''} totaling ${formatCurrency(
          accountInvestments.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
          selectedCountry
        )}.\n\nDeleting this account will also delete ALL ${investmentCount} investment${investmentCount > 1 ? 's' : ''} inside it.\n\nThis action cannot be undone. Are you sure?`
      : 'Are you sure you want to remove this account?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      console.log(`[Delete Account] Starting cascade delete for account ${accountId}`);
      console.log(`[Delete Account] Found ${investmentCount} investments to delete`);

      // ⭐ ATOMIC CASCADE DELETE using Firestore batch
      // This ensures all deletes succeed or none do (data consistency)
      const batch = writeBatch(db);

      // 1. Delete all investments linked to this account
      for (const investment of accountInvestments) {
        const investmentRef = doc(db, 'users', currentUser.uid, 'investments', investment.id);
        batch.delete(investmentRef);
        console.log(`[Delete Account] Queued investment deletion: ${investment.name} (${investment.id})`);
      }

      // 2. Delete the account itself
      const accountRef = doc(db, 'users', currentUser.uid, 'accounts', accountId);
      batch.delete(accountRef);
      console.log(`[Delete Account] Queued account deletion: ${accountId}`);

      // 3. Commit the batch (atomic operation - all or nothing)
      await batch.commit();
      console.log(`[Delete Account] Successfully deleted account and ${investmentCount} investments`);

      // Only update state after successful Firebase operation
      setAccounts(prev => prev.filter(acc => acc.id !== accountId));
      setInvestments(prev => prev.filter(inv => inv.accountId !== accountId));

      alert(`Account deleted successfully${investmentCount > 0 ? ` along with ${investmentCount} investment${investmentCount > 1 ? 's' : ''}` : ''}.`);
    } catch (error) {
      console.error('[Delete Account] Error during cascade delete:', error);
      alert('Failed to remove account: ' + error.message + '\n\nNo data was deleted (operation rolled back).');
    }
  };

  const handleUpdateAccount = async (accountId, updates) => {
    if (!currentUser) return;

    try {
      const accountRef = doc(db, 'users', currentUser.uid, 'accounts', accountId);
      await updateDoc(accountRef, updates);
      
      // Only update state after successful Firebase operation
      setAccounts(prev => prev.map(acc => 
        acc.id === accountId ? { ...acc, ...updates } : acc
      ));
      console.log('Account updated successfully');
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Failed to update account: ' + error.message);
    }
  };

  // NEW: Cryptocurrency management functions
  const handleAddCrypto = async (cryptoData) => {
    if (!currentUser) {
      alert('Please log in to add cryptocurrency');
      return;
    }

    try {
      const cryptoRef = collection(db, 'users', currentUser.uid, 'cryptocurrencies');
      const docRef = await addDoc(cryptoRef, cryptoData);
      
      // Only update state after successful Firebase operation
      setCryptocurrencies(prev => [...prev, { id: docRef.id, ...cryptoData }]);
      console.log('Cryptocurrency added successfully');
    } catch (error) {
      console.error('Error adding crypto:', error);
      alert('Failed to add cryptocurrency: ' + error.message);
      throw error; // Re-throw so component can handle it
    }
  };

  const handleRemoveCrypto = async (cryptoId) => {
    if (!currentUser) return;
    
    if (!window.confirm('Are you sure you want to remove this cryptocurrency?')) {
      return;
    }

    try {
      const cryptoRef = doc(db, 'users', currentUser.uid, 'cryptocurrencies', cryptoId);
      await deleteDoc(cryptoRef);
      
      // Only update state after successful Firebase operation
      setCryptocurrencies(prev => prev.filter(crypto => crypto.id !== cryptoId));
      console.log('Cryptocurrency removed successfully');
    } catch (error) {
      console.error('Error removing crypto:', error);
      alert('Failed to remove cryptocurrency: ' + error.message);
    }
  };

  const handleUpdateCrypto = async (cryptoId, updates) => {
    if (!currentUser) return;

    try {
      const cryptoRef = doc(db, 'users', currentUser.uid, 'cryptocurrencies', cryptoId);
      await updateDoc(cryptoRef, updates);
      
      // Only update state after successful Firebase operation
      setCryptocurrencies(prev => prev.map(crypto => 
        crypto.id === cryptoId ? { ...crypto, ...updates } : crypto
      ));
      console.log('Cryptocurrency updated successfully');
    } catch (error) {
      console.error('Error updating crypto:', error);
      alert('Failed to update cryptocurrency: ' + error.message);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const runAIAnalysis = () => {
    const userPlan = userProfile?.plan || 'free';

    if (userPlan === 'free' && aiAnalysis) {
      setShowUpgradeModal(true);
      return;
    }

    setAnalyzing(true);

    setTimeout(() => {
      // Aggregate all holdings (investments, crypto, accounts)
      const aggregated = aggregatePortfolioHoldings(investments, cryptocurrencies, accounts);
      const { holdings, totalValue, cryptoValue, accountsValue, investmentsValue } = aggregated;

      // Calculate risk metrics
      const expectedReturn = calculateExpectedReturn(holdings);
      const volatility = calculateVolatility(holdings);
      const sharpeRatio = calculateSharpeRatio(expectedReturn, 3, volatility);

      // Calculate diversification score
      const diversification = calculateDiversificationScore(portfolioData);

      // Determine risk level
      const stockAllocation = portfolioData.find(p => p.name === 'Stocks')?.allocation || 0;
      let riskScore = 'Low';
      if (stockAllocation > 70 || volatility > 16) riskScore = 'High';
      else if (stockAllocation > 40 || volatility > 10) riskScore = 'Medium';

      // Generate scenario projections
      const yearsToRetirement = retirementGoals.retirementAge - retirementGoals.currentAge;
      const scenarios = generateScenarioProjections(
        totalValue,
        retirementGoals.monthlyContribution,
        yearsToRetirement > 0 ? yearsToRetirement : 10,
        expectedReturn,
        volatility
      );

      // Generate AI commentary
      const commentary = generateAICommentary({
        portfolioData,
        totalValue,
        cryptoValue,
        accountsValue,
        expectedReturn,
        volatility,
        sharpeRatio,
        diversificationScore: diversification.score
      });

      // Generate country-specific recommendations
      const countryRecommendations = generateCountrySpecificRecommendations(
        selectedCountry,
        accounts
      );

      // Generate general recommendations
      const recommendations = [];

      if (stockAllocation > 80) {
        recommendations.push({
          title: 'Reduce Stock Concentration',
          description: 'Your portfolio has heavy stock concentration. Consider reallocating 15-20% to bonds for better risk management.',
          priority: 'high',
          impact: `Reduce volatility by 3-5%`,
          countrySpecific: false
        });
      }

      if (retirementGoals.monthlyContribution < 500 && totalValue < 100000) {
        recommendations.push({
          title: 'Increase Monthly Contributions',
          description: `Increasing your monthly contribution by ${currencySymbol}200 could add ${currencySymbol}180,000 to your retirement savings over 30 years.`,
          priority: 'medium',
          impact: `+${currencySymbol}180,000 at retirement`,
          countrySpecific: false
        });
      }

      if (diversification.score < 60) {
        recommendations.push({
          title: 'Improve Diversification',
          description: `Your diversification score of ${diversification.score} is below optimal. Consider spreading investments across more asset classes with allocations of at least 10% each.`,
          priority: 'high',
          impact: 'Reduce risk by 20-30%',
          countrySpecific: false
        });
      }

      if (sharpeRatio < 0.5) {
        recommendations.push({
          title: 'Optimize Risk-Return Balance',
          description: `Your Sharpe ratio of ${sharpeRatio} indicates poor risk-adjusted returns. Review underperforming assets and consider rebalancing toward higher-return investments.`,
          priority: 'medium',
          impact: 'Improve returns without added risk',
          countrySpecific: false
        });
      }

      // Merge general and country-specific recommendations
      const allRecommendations = [...recommendations, ...countryRecommendations];

      const monthsToGoal = (retirementGoals.retirementAge - retirementGoals.currentAge) * 12;

      // Calculate asset breakdown by category from all holdings
      // This ensures crypto and investments are not double-counted
      const assetBreakdown = {};

      // Add all holdings from aggregated data (includes both investments and crypto)
      holdings.forEach(holding => {
        const category = holding.category || 'Other';
        assetBreakdown[category] = (assetBreakdown[category] || 0) + holding.amount;
      });

      const analysisData = {
        riskScore,
        diversificationScore: diversification.score,
        diversificationRating: diversification.rating,
        expectedReturn,
        volatility,
        sharpeRatio,
        scenarios,
        commentary,
        recommendations: allRecommendations,
        projectedValue: scenarios.median,
        monthsToGoal,
        totalValue,
        cryptoValue,
        accountsValue,
        investmentsValue,
        assetBreakdown,
        timestamp: Date.now() // Add timestamp for caching
      };

      setAiAnalysis(analysisData);

      // Save to localStorage for persistence across navigation
      try {
        const cacheKey = `builtin_analysis_${currentUser.uid}`;
        localStorage.setItem(cacheKey, JSON.stringify(analysisData));
        console.log('[Built-in Analysis] Analysis cached to localStorage');
      } catch (err) {
        console.error('[Built-in Analysis] Failed to cache analysis:', err);
        // Non-critical error, continue
      }

      setAnalyzing(false);
    }, 2000);
  };

  const totalValue = portfolioData.reduce((sum, item) => sum + item.value, 0);
  console.log('[Portfolio] UI render - portfolioData:', portfolioData);
  console.log('[Portfolio] UI render - totalValue:', totalValue, currencySymbol);

  // Calculate retirement projections
  // Calculate actual portfolio value from investments and crypto
  const actualPortfolioValue = totalValue; // Use the totalValue already calculated from portfolioData

  // Calculate total monthly contributions from all accounts
  const totalMonthlyContribution = accounts.reduce((sum, acc) => {
    const amount = acc.contributionAmount || 0;
    const frequency = acc.contributionFrequency || 'monthly';
    return sum + (frequency === 'monthly' ? amount : amount / 12);
  }, 0);

  // Calculate weighted average expected return from actual portfolio
  const portfolioExpectedReturn = investments.length > 0
    ? investments.reduce((sum, inv) => sum + inv.return * inv.amount, 0) /
      investments.reduce((sum, inv) => sum + inv.amount, 0)
    : retirementGoals.expectedReturn;

  // Build projection data with account-by-account breakdown
  const projectionData = [];
  // ⭐ LIVE PRICING: Pass priceMap to get correct market values
  const accountsWithBalances = getAccountsWithBalances(accounts, investments, displayCurrency, exchangeRates, priceMap);

  for (let i = 0; i <= 30; i++) {
    const year = 2025 + i;
    const age = retirementGoals.currentAge + i;
    const years = i;

    // Calculate total portfolio future value
    const futureValue = actualPortfolioValue * Math.pow(1 + portfolioExpectedReturn / 100, years) +
      totalMonthlyContribution * 12 * ((Math.pow(1 + portfolioExpectedReturn / 100, years) - 1) / (portfolioExpectedReturn / 100));

    const dataPoint = {
      year,
      age,
      value: Math.round(futureValue),
      contributions: actualPortfolioValue + totalMonthlyContribution * 12 * years,
      growth: Math.round(futureValue - (actualPortfolioValue + totalMonthlyContribution * 12 * years))
    };

    // Add per-account projections
    accountsWithBalances.forEach(account => {
      const accountContribution = account.contributionAmount || 0;
      const monthlyContribution = account.contributionFrequency === 'monthly' ? accountContribution : accountContribution / 12;

      // Use account-specific expected return (weighted avg of investments in that account)
      const accountInvestments = investments.filter(inv => inv.accountId === account.id);
      const accountReturn = accountInvestments.length > 0
        ? accountInvestments.reduce((sum, inv) => sum + inv.return * inv.amount, 0) /
          accountInvestments.reduce((sum, inv) => sum + inv.amount, 0)
        : portfolioExpectedReturn;

      const accountStartValue = account.calculatedBalance || 0;
      const accountFutureValue = accountStartValue * Math.pow(1 + accountReturn / 100, years) +
        monthlyContribution * 12 * ((Math.pow(1 + accountReturn / 100, years) - 1) / (accountReturn / 100));

      dataPoint[account.name] = Math.round(accountFutureValue);
    });

    projectionData.push(dataPoint);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header with Country Selector */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              {/* Mobile Hamburger Menu */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Toggle menu"
              >
                {sidebarOpen ? (
                  <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                )}
              </button>

              <button
                onClick={onNavigateToLanding}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity cursor-pointer group"
                title="Back to Home"
              >
                <TrendingUp className="w-8 h-8 text-blue-600 group-hover:scale-110 transition-transform" />
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    PlanLens
                  </h1>
                  <p className="text-xs text-gray-500 dark:text-gray-400">AI-Powered Wealth Building and Financial Planning</p>
                </div>
              </button>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-wrap">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDark ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600" />
                )}
              </button>

              {/* Admin Badge */}
              {userProfile?.isAdmin && (
                <div className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full">
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span className="text-xs font-bold text-white">ADMIN</span>
                </div>
              )}

              {/* Premium Badge */}
              {userProfile?.plan === 'premium' ? (
                <div className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full">
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  <span className="text-xs font-bold text-white whitespace-nowrap">PREMIUM</span>
                </div>
              ) : (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                  <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">Upgrade</span>
                </button>
              )}

              {/* Saving indicator - always reserve space to prevent layout shift */}
              <div className={`hidden sm:flex items-center space-x-2 text-sm transition-opacity duration-200 ${
                saving ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`} style={{ minWidth: '90px' }}>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">Saving...</span>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 max-w-[120px] sm:max-w-none truncate">{currentUser?.email}</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar Navigation */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } w-64 overflow-y-auto`}
        style={{ top: '73px' }} // Adjust based on header height
      >
        <nav className="p-4 space-y-1">
          {/* Dashboard */}
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <PieChartIcon className="w-5 h-5" />
            <span>Dashboard</span>
          </button>

          {/* Wealth Group */}
          <div className="mt-4">
            <button
              onClick={() => setExpandedGroups(prev =>
                prev.includes('wealth') ? prev.filter(g => g !== 'wealth') : [...prev, 'wealth']
              )}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
            >
              <div className="flex items-center space-x-2">
                <Wallet className="w-4 h-4" />
                <span>Wealth</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroups.includes('wealth') ? 'rotate-90' : ''}`} />
            </button>
            {expandedGroups.includes('wealth') && (
              <div className="ml-4 mt-1 space-y-1">
                {[
                  { id: 'networth', label: 'Net Worth', icon: TrendingUp, premium: true },
                  { id: 'wealth-tracking', label: 'Wealth Tracking', icon: LineChart, premium: true },
                  { id: 'portfolio', label: 'Assets', icon: Activity },
                  { id: 'liabilities', label: 'Liabilities', icon: CreditCard, premium: true }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.premium && userProfile?.plan !== 'premium') {
                        setShowUpgradeModal(true);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.premium && userProfile?.plan !== 'premium' && (
                      <Lock className="w-3 h-3 text-yellow-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Investments Group */}
          <div className="mt-2">
            <button
              onClick={() => setExpandedGroups(prev =>
                prev.includes('investments') ? prev.filter(g => g !== 'investments') : [...prev, 'investments']
              )}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-4 h-4" />
                <span>Investments</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroups.includes('investments') ? 'rotate-90' : ''}`} />
            </button>
            {expandedGroups.includes('investments') && (
              <div className="ml-4 mt-1 space-y-1">
                {[
                  { id: 'accounts', label: 'Accounts', icon: DollarSign },
                  { id: 'portfolio-analysis', label: 'Analysis', icon: Activity, premium: true }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.premium && userProfile?.plan !== 'premium') {
                        setShowUpgradeModal(true);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.premium && userProfile?.plan !== 'premium' && (
                      <Lock className="w-3 h-3 text-yellow-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Planning Group */}
          <div className="mt-2">
            <button
              onClick={() => setExpandedGroups(prev =>
                prev.includes('planning') ? prev.filter(g => g !== 'planning') : [...prev, 'planning']
              )}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
            >
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4" />
                <span>Planning</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroups.includes('planning') ? 'rotate-90' : ''}`} />
            </button>
            {expandedGroups.includes('planning') && (
              <div className="ml-4 mt-1 space-y-1">
                {[
                  { id: 'goals', label: 'Financial Goals', icon: Target, premium: false },
                  { id: 'retirement', label: 'Retirement', icon: Calendar, premium: true },
                  { id: 'budget-overview', label: 'Budgeting', icon: Receipt, premium: false }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.premium && userProfile?.plan !== 'premium') {
                        setShowUpgradeModal(true);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.premium && userProfile?.plan !== 'premium' && (
                      <Lock className="w-3 h-3 text-yellow-500" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Insights */}
          <button
            onClick={() => {
              if (userProfile?.plan !== 'premium') {
                setShowUpgradeModal(true);
              } else {
                setActiveTab('ai-analysis');
              }
            }}
            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mt-2 ${
              activeTab === 'ai-analysis'
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Brain className="w-5 h-5" />
            <span className="flex-1 text-left">Insights</span>
            {userProfile?.plan !== 'premium' && (
              <Lock className="w-4 h-4 text-yellow-500" />
            )}
          </button>

          {/* More Group */}
          <div className="mt-2">
            <button
              onClick={() => setExpandedGroups(prev =>
                prev.includes('more') ? prev.filter(g => g !== 'more') : [...prev, 'more']
              )}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200"
            >
              <div className="flex items-center space-x-2">
                <Settings className="w-4 h-4" />
                <span>More</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroups.includes('more') ? 'rotate-90' : ''}`} />
            </button>
            {expandedGroups.includes('more') && (
              <div className="ml-4 mt-1 space-y-1">
                {[
                  { id: 'profile', label: 'Profile', icon: User },
                  { id: 'data-cleanup', label: 'Data Cleanup', icon: AlertCircle },
                  { id: 'help-learn', label: 'Help & Learn', icon: BookOpen },
                  { id: 'support', label: 'Support', icon: LifeBuoy }
                ].map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Tab - REDESIGNED */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* View Toggle - Vanguard vs Classic */}
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setDashboardView('vanguard')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    dashboardView === 'vanguard'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Table View
                </button>
                <button
                  onClick={() => setDashboardView('classic')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    dashboardView === 'classic'
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Card View
                </button>
              </div>
            </div>

            {/* Vanguard-style Dashboard */}
            {dashboardView === 'vanguard' && (
              <VanguardPortfolioDashboard
                investments={investments}
                accounts={accounts}
                cryptocurrencies={cryptocurrencies}
                wealthSnapshots={wealthSnapshots}
                liabilities={[]}
                exchangeRates={exchangeRates}
                isDark={isDark}
                onEditInvestment={handleEditInvestmentClick}
                onAddToPosition={handleOpenAddToPosition}
                onDeleteInvestment={handleDeleteInvestment}
                onAddInvestment={() => setShowAddInvestment(true)}
                onUpdateInvestmentSectorRegion={handleUpdateInvestmentSectorRegion}
                portfolioName="My Portfolio"
              />
            )}

            {/* Classic Card-based Dashboard */}
            {dashboardView === 'classic' && (
            <>
            {/* Hero Metrics Section */}
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl text-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Portfolio Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs sm:text-sm mb-2">Total Portfolio Value</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">{currencySymbol}{totalValue.toLocaleString()}</p>
                  <p className="text-blue-200 text-xs mt-2">All investments combined</p>
                </div>
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs sm:text-sm mb-2">Total Holdings</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">{investments.length + cryptocurrencies.length}</p>
                  <p className="text-blue-200 text-xs mt-2">Individual positions</p>
                </div>
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs sm:text-sm mb-2">Accounts</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold break-words">{accounts.length}</p>
                  <p className="text-blue-200 text-xs mt-2">Financial accounts</p>
                </div>
              </div>
            </div>

            {/* ⭐ PRICING: Portfolio Summary with Real-Time Prices (Phase 2) */}
            {investments.length > 0 && (
              <SimplePortfolioSummary
                {...portfolioMetrics}
                currency={displayCurrency}
                onRefresh={portfolioMetrics.refresh}
              />
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Large Allocation Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Asset Allocation</h3>
                {totalValue > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={portfolioData.filter(p => p.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={false}
                          outerRadius={140}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {portfolioData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${currencySymbol}${value.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>

                    {/* Allocation Legend */}
                    <div className="grid grid-cols-2 gap-3 mt-6">
                      {portfolioData.filter(p => p.value > 0).map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {currencySymbol}{item.value.toLocaleString()} ({item.allocation}%)
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96 text-center">
                    <PieChartIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">No portfolio data yet</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Add investments to see your allocation</p>
                  </div>
                )}
              </div>

              {/* Account Breakdown */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Accounts</h3>
                {accounts.length > 0 ? (
                  <div className="space-y-4">
                    {getAccountsWithBalances(accounts, investments, getCurrencyCode(selectedCountry), exchangeRates, priceMap).slice(0, 5).map((account, index) => (
                      <div key={account.id} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                        <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white truncate">{account.name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{account.type} • {account.holdingsCount} holdings</p>
                          </div>
                        </div>
                        <div className="text-right ml-2 sm:ml-4 flex-shrink-0">
                          <p className="text-sm sm:text-base font-bold text-gray-900 dark:text-white break-words">{currencySymbol}{account.calculatedBalance.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    {accounts.length > 5 && (
                      <button
                        onClick={() => setActiveTab('accounts')}
                        className="w-full py-3 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium transition-colors"
                      >
                        View All {accounts.length} Accounts →
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <DollarSign className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">No accounts yet</p>
                    <button
                      onClick={() => setActiveTab('accounts')}
                      className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add Account
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Top Holdings Section */}
            <TopHoldings
              investments={investments}
              cryptocurrencies={cryptocurrencies}
              totalValue={totalValue}
              currencySymbol={currencySymbol}
              isDark={isDark}
              exchangeRates={exchangeRates}
              selectedCountry={selectedCountry}
            />

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <button
                onClick={() => setActiveTab('portfolio')}
                className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Plus className="w-8 h-8 mb-2" />
                <p className="font-bold text-lg">Add Investment</p>
                <p className="text-sm text-blue-100 mt-1">Grow your portfolio</p>
              </button>

              <button
                onClick={() => {
                  if (userProfile?.plan !== 'premium') {
                    setShowUpgradeModal(true);
                  } else {
                    setActiveTab('ai-analysis');
                  }
                }}
                className="p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl relative"
              >
                <Brain className="w-8 h-8 mb-2" />
                <p className="font-bold text-lg">Financial Health</p>
                <p className="text-sm text-purple-100 mt-1">Complete analysis</p>
                {userProfile?.plan !== 'premium' && (
                  <div className="absolute top-4 right-4">
                    <Crown className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
              </button>

              <button
                onClick={() => {
                  if (userProfile?.plan !== 'premium') {
                    setShowUpgradeModal(true);
                  } else {
                    setActiveTab('retirement');
                  }
                }}
                className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl relative"
              >
                <Target className="w-8 h-8 mb-2" />
                <p className="font-bold text-lg">Retirement Plan</p>
                <p className="text-sm text-green-100 mt-1">Plan your future</p>
                {userProfile?.plan !== 'premium' && (
                  <div className="absolute top-4 right-4">
                    <Crown className="w-5 h-5 text-yellow-400" />
                  </div>
                )}
              </button>
            </div>
            </>
            )}
          </div>
        )}

        {/* Accounts Tab - Updated */}
        {activeTab === 'accounts' && (
          <div className="space-y-6">
            <AccountTypeManager
              userAccounts={accounts}
              investments={investments}
              exchangeRates={exchangeRates}
              onAddAccount={handleAddAccount}
              onRemoveAccount={handleRemoveAccount}
              onUpdateAccount={handleUpdateAccount}
            />
          </div>
        )}

        {/* NEW: Portfolio Analysis Tab */}
        {activeTab === 'portfolio-analysis' && (
          <div className="space-y-6">
            <PortfolioAnalysis
              userProfile={userProfile}
              onUpgrade={() => setShowUpgradeModal(true)}
              investments={investments}
            />
          </div>
        )}

        {/* Portfolio Tab - Unified Investments + Crypto */}
        {activeTab === 'portfolio' && (
          <div className="space-y-6">
            {/* Portfolio Header with Stats */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white shadow-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-3xl font-bold mb-2">Portfolio</h2>
                  <p className="text-blue-100">Your complete investment holdings</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Download Template */}
                  <button
                    onClick={handleDownloadInvestmentsTemplate}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
                    title="Download CSV template"
                  >
                    <Download className="w-4 h-4" />
                    <span className="text-sm hidden sm:inline">Template</span>
                  </button>

                  {/* Import CSV */}
                  <button
                    onClick={() => setShowImportInvestmentsModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
                    title="Import investments from CSV"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="text-sm hidden sm:inline">Import CSV</span>
                  </button>

                  {/* Export CSV */}
                  {investments.length > 0 && (
                    <button
                      onClick={handleExportInvestmentsCSV}
                      className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all whitespace-nowrap"
                      title="Export investments to CSV"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm hidden sm:inline">Export CSV</span>
                    </button>
                  )}

                  {/* Add Investment */}
                  <button
                    onClick={() => setShowAddInvestment(true)}
                    className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all shadow-md hover:shadow-lg font-semibold whitespace-nowrap"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="text-sm sm:text-base">Add Investment</span>
                  </button>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-blue-100 text-sm mb-1">Total Value</p>
                  <p className="text-4xl font-bold">
                    {currencySymbol}{totalValue.toLocaleString()}
                  </p>
                  <p className="text-blue-200 text-xs mt-2">All investments combined</p>
                </div>
                <div>
                  <p className="text-blue-100 text-sm mb-1">Total Holdings</p>
                  <p className="text-4xl font-bold">{investments.length + cryptocurrencies.length}</p>
                  <p className="text-blue-200 text-xs mt-2">Individual positions</p>
                </div>
              </div>
            </div>

            {/* View Selector */}
            <div className="flex items-center space-x-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPortfolioView('all')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    portfolioView === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  All Holdings
                </button>
                <button
                  onClick={() => setPortfolioView('byAccount')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    portfolioView === 'byAccount'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  By Account
                </button>
                <button
                  onClick={() => setPortfolioView('byCategory')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    portfolioView === 'byCategory'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  By Asset Class
                </button>
              </div>
            </div>

            {/* Holdings Display */}
            {portfolioView === 'all' && (
              <div className="space-y-6">
                {/* All Holdings - Unified View */}
                {(investments.length > 0 || cryptocurrencies.length > 0) ? (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Activity className="w-5 h-5 mr-2" />
                      All Holdings ({investments.length + cryptocurrencies.length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Traditional Investments - use portfolioMetrics for corrected prices */}
                      {portfolioMetrics.investmentDetails.map(investment => (
                        <div key={investment.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{investment.name}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{investment.category}</p>
                              {investment.accountName && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  📁 {investment.accountName}
                                </p>
                              )}
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleOpenAddToPosition(investment)}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                title="Add to position"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditInvestmentClick(investment)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                title="Edit investment"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvestment(investment.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Delete investment"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Interactive Investor Style Display */}
                          {investment.quantity && investment.quantity > 0 ? (
                            <div className="space-y-3">
                              {/* Holding Grid - Quantity & Average Price */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Quantity</p>
                                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                                    {formatQuantity(investment.quantity, investment.category)}
                                  </p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Average Price</p>
                                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                                    {getCurrencySymbolByCode(investment.currency || 'USD')}{(investment.averageCostBasis || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}
                                  </p>
                                </div>
                              </div>

                              {/* Gain/Loss - Calculate from market price or show expected return */}
                              {investment.currentPrice ? (
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Gain/Loss</p>
                                    {(() => {
                                      // Use pre-calculated values from portfolioMetrics when available
                                      const gain = investment.gainLoss || 0;
                                      const gainPercent = investment.gainLossPercent || 0;
                                      const isPositive = gain >= 0;
                                      return (
                                        <p className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                          <span className="text-sm">{isPositive ? '↑' : '↓'} {Math.abs(gainPercent).toFixed(2)}%</span>
                                          <span className="ml-2">{getCurrencySymbolByCode(investment.currency || 'USD')}{Math.abs(gain).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                        </p>
                                      );
                                    })()}
                                  </div>
                                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Market Price</p>
                                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                                      {getCurrencySymbolByCode(investment.currency || 'USD')}{investment.currentPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}
                                      {investment.priceData?.changePercent !== undefined && (
                                        <span className={`ml-1 text-xs ${investment.priceData.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {investment.priceData.changePercent >= 0 ? '+' : ''}{investment.priceData.changePercent.toFixed(2)}%
                                        </span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Expected Return</p>
                                  <p className="text-lg font-bold text-green-600">{investment.return}%</p>
                                </div>
                              )}

                              {/* Cost Basis & Market Value */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost Basis</p>
                                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                                    {getCurrencySymbolByCode(investment.currency || 'USD')}{(investment.costBasis || investment.totalCostBasis || investment.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </p>
                                </div>
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Market Value</p>
                                  <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                                    {getCurrencySymbolByCode(investment.currency || 'USD')}{(investment.marketValue || investment.costBasis || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </p>
                                </div>
                              </div>

                              {/* Risk Level */}
                              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Risk Level:</span>
                                <span className={`font-semibold ${
                                  investment.risk === 'High' ? 'text-red-600' :
                                  investment.risk === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                                }`}>{investment.risk}</span>
                              </div>
                            </div>
                          ) : (
                            /* Legacy display for non-quantity investments */
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                              <span className="font-semibold text-gray-900 dark:text-white">
                                {(() => {
                                  // ⭐ MULTI-CURRENCY: Show native currency with optional conversion
                                  const investmentCurrency = investment.currency || getCurrencyCode(selectedCountry);
                                  const displayCurrency = getCurrencyCode(selectedCountry);
                                  const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
                                  const amount = investment.amount;

                                  // If same currency, just show once
                                  if (investmentCurrency === displayCurrency) {
                                    return `${nativeSymbol}${amount.toLocaleString()}`;
                                  }

                                  // Different currency - show native + converted
                                  if (exchangeRates && exchangeRates.rates) {
                                    try {
                                      const fromRate = exchangeRates.rates[investmentCurrency];
                                      const toRate = exchangeRates.rates[displayCurrency];
                                      if (fromRate && toRate) {
                                        const converted = (amount / fromRate) * toRate;
                                        const displaySymbol = getCurrencySymbol(selectedCountry);
                                        return (
                                          <>
                                            <span className="text-blue-600 dark:text-blue-400">{nativeSymbol}{amount.toLocaleString()} {investmentCurrency}</span>
                                            <span className="text-sm text-gray-500 dark:text-gray-400"> (≈ {displaySymbol}{Math.round(converted).toLocaleString()})</span>
                                          </>
                                        );
                                      }
                                    } catch (e) {
                                      // Fallback
                                    }
                                  }

                                  // Fallback: show native currency
                                  return `${nativeSymbol}${amount.toLocaleString()} ${investmentCurrency}`;
                                })()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Expected Return:</span>
                              <span className="font-semibold text-green-600">{investment.return}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Risk Level:</span>
                              <span className={`font-semibold ${
                                investment.risk === 'High' ? 'text-red-600' :
                                investment.risk === 'Medium' ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {investment.risk}
                              </span>
                            </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Cryptocurrency Holdings - Inline */}
                      {cryptocurrencies.map(crypto => (
                        <div key={crypto.id} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="font-bold text-gray-900 dark:text-white">{crypto.name || crypto.symbol}</h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Cryptocurrency</p>
                            </div>
                            <button
                              onClick={() => handleRemoveCrypto(crypto.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                              <span className="font-semibold text-gray-900 dark:text-white">{currencySymbol}{(crypto.value || crypto.balance || 0).toLocaleString()}</span>
                            </div>
                            {crypto.expectedReturn && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Expected Return:</span>
                                <span className="font-semibold text-green-600">{crypto.expectedReturn}%</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                    <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 dark:text-gray-400 mb-2">No holdings yet</p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">Add your first investment to get started!</p>
                  </div>
                )}
              </div>
            )}

            {/* By Account View */}
            {portfolioView === 'byAccount' && (
              <div className="space-y-6">
                {(() => {
                  const displayCurrency = getCurrencyCode(selectedCountry);
                  // Use portfolioMetrics.investmentDetails for corrected prices
                  const grouped = groupInvestmentsByAccount(portfolioMetrics.investmentDetails, accounts, displayCurrency, exchangeRates);
                  const accountGroups = Object.entries(grouped).filter(([key]) => key !== 'unassigned');
                  const unassigned = grouped.unassigned || [];

                  return (
                    <>
                      {accountGroups.map(([accountId, data]) => (
                        <div key={accountId} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                                <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                                {data.account.name}
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {data.account.type} • {data.account.accountPurpose || 'Account'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {currencySymbol}{data.totalValue.toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {data.investments.length} holdings
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.investments.map(investment => (
                              <div key={investment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">{investment.name}</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{investment.category}</p>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => handleOpenAddToPosition(investment)}
                                      className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      title="Add to position"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleEditInvestmentClick(investment)}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteInvestment(investment.id)}
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                {/* Position info if available */}
                                {investment.quantity && investment.quantity > 0 && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {formatQuantity(investment.quantity, investment.category)} {getUnitLabel(investment.category)} @ {getCurrencySymbolByCode(investment.currency || 'USD')}{(investment.averageCostBasis || 0).toFixed(2)}
                                  </p>
                                )}
                                {/* Live pricing info */}
                                {investment.currentPrice && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    <span>Market: {getCurrencySymbolByCode(investment.currency || 'USD')}{investment.currentPrice.toFixed(2)}</span>
                                    {investment.priceData?.changePercent !== undefined && (
                                      <span className={`ml-2 ${investment.priceData.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {investment.priceData.changePercent >= 0 ? '+' : ''}{investment.priceData.changePercent.toFixed(2)}%
                                      </span>
                                    )}
                                  </p>
                                )}
                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                  {(() => {
                                    // Show market value if available, otherwise cost basis
                                    const investmentCurrency = investment.currency || displayCurrency;
                                    const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
                                    const amount = investment.amount;

                                    // If same currency, just show once
                                    if (investmentCurrency === displayCurrency) {
                                      return `${nativeSymbol}${amount.toLocaleString()}`;
                                    }

                                    // Different currency - show native + converted
                                    if (exchangeRates && exchangeRates.rates) {
                                      try {
                                        const fromRate = exchangeRates.rates[investmentCurrency];
                                        const toRate = exchangeRates.rates[displayCurrency];
                                        if (fromRate && toRate) {
                                          const converted = (amount / fromRate) * toRate;
                                          const displaySymbol = getCurrencySymbol(selectedCountry);
                                          return (
                                            <>
                                              <span className="text-blue-600 dark:text-blue-400">{nativeSymbol}{amount.toLocaleString()} {investmentCurrency}</span>
                                              <br />
                                              <span className="text-xs text-gray-500 dark:text-gray-400">(≈ {displaySymbol}{Math.round(converted).toLocaleString()})</span>
                                            </>
                                          );
                                        }
                                      } catch (e) {
                                        // Fallback
                                      }
                                    }

                                    // Fallback: show native currency
                                    return `${nativeSymbol}${amount.toLocaleString()} ${investmentCurrency}`;
                                  })()}
                                </p>
                                <p className="text-xs text-green-600">{investment.return}% expected return</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}

                      {unassigned.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                          <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-4">
                            Unassigned Investments ({unassigned.length})
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {unassigned.map(investment => (
                              <div key={investment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                  <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">{investment.name}</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{investment.category}</p>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => handleOpenAddToPosition(investment)}
                                      className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                      title="Add to position"
                                    >
                                      <Plus className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleEditInvestmentClick(investment)}
                                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteInvestment(investment.id)}
                                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                  {(() => {
                                    // ⭐ MULTI-CURRENCY: Show native currency with optional conversion
                                    const investmentCurrency = investment.currency || displayCurrency;
                                    const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
                                    const amount = investment.amount;

                                    // If same currency, just show once
                                    if (investmentCurrency === displayCurrency) {
                                      return `${nativeSymbol}${amount.toLocaleString()}`;
                                    }

                                    // Different currency - show native + converted
                                    if (exchangeRates && exchangeRates.rates) {
                                      try {
                                        const fromRate = exchangeRates.rates[investmentCurrency];
                                        const toRate = exchangeRates.rates[displayCurrency];
                                        if (fromRate && toRate) {
                                          const converted = (amount / fromRate) * toRate;
                                          const displaySymbol = getCurrencySymbol(selectedCountry);
                                          return (
                                            <>
                                              <span className="text-blue-600 dark:text-blue-400">{nativeSymbol}{amount.toLocaleString()} {investmentCurrency}</span>
                                              <br />
                                              <span className="text-xs text-gray-500 dark:text-gray-400">(≈ {displaySymbol}{Math.round(converted).toLocaleString()})</span>
                                            </>
                                          );
                                        }
                                      } catch (e) {
                                        // Fallback
                                      }
                                    }

                                    // Fallback: show native currency
                                    return `${nativeSymbol}${amount.toLocaleString()} ${investmentCurrency}`;
                                  })()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {accountGroups.length === 0 && unassigned.length === 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                          <DollarSign className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600 dark:text-gray-400">No investments to display</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* By Category View */}
            {portfolioView === 'byCategory' && (
              <div className="space-y-6">
                {(() => {
                  const displayCurrency = getCurrencyCode(selectedCountry);
                  // Use portfolioMetrics.investmentDetails for corrected prices
                  const grouped = groupInvestmentsByCategory(portfolioMetrics.investmentDetails, displayCurrency, exchangeRates);
                  const categoryGroups = Object.entries(grouped).sort((a, b) => b[1].totalValue - a[1].totalValue);

                  const categoryIcons = {
                    'Stocks': '📈',
                    'Bonds': '🏛️',
                    'Real Estate': '🏠',
                    'Commodities': '🥇',
                    'Cryptocurrency': '🪙',
                    'Cash': '💵'
                  };

                  const totalPortfolioValue = Object.values(grouped).reduce((sum, cat) => sum + cat.totalValue, 0);

                  return (
                    <>
                      {categoryGroups.map(([category, data]) => {
                        const percentage = totalPortfolioValue > 0
                          ? ((data.totalValue / totalPortfolioValue) * 100).toFixed(1)
                          : 0;

                        return (
                          <div key={category} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                                  <span className="mr-2 text-2xl">{categoryIcons[category] || '📊'}</span>
                                  {category}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {percentage}% of portfolio • {data.investments.length} holdings
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                  {currencySymbol}{data.totalValue.toLocaleString()}
                                </p>
                                <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full transition-all"
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {data.investments.map(investment => (
                                <div key={investment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="font-semibold text-gray-900 dark:text-white">{investment.name}</p>
                                      {investment.accountName && (
                                        <p className="text-xs text-blue-600 dark:text-blue-400">
                                          {investment.accountName}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex space-x-1">
                                      <button
                                        onClick={() => handleOpenAddToPosition(investment)}
                                        className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                        title="Add to position"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => handleEditInvestmentClick(investment)}
                                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                        title="Edit"
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteInvestment(investment.id)}
                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                        title="Delete"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Position info if available */}
                                  {investment.quantity && investment.quantity > 0 && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                      {formatQuantity(investment.quantity, investment.category)} {getUnitLabel(investment.category)} @ {getCurrencySymbolByCode(investment.currency || 'USD')}{(investment.averageCostBasis || 0).toFixed(2)}
                                    </p>
                                  )}
                                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                                    {(() => {
                                      // Show native currency with optional conversion
                                      const investmentCurrency = investment.currency || displayCurrency;
                                      const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
                                      const amount = investment.totalCostBasis || investment.amount;

                                      // If same currency, just show once
                                      if (investmentCurrency === displayCurrency) {
                                        return `${nativeSymbol}${amount.toLocaleString()}`;
                                      }

                                      // Different currency - show native + converted
                                      if (exchangeRates && exchangeRates.rates) {
                                        try {
                                          const fromRate = exchangeRates.rates[investmentCurrency];
                                          const toRate = exchangeRates.rates[displayCurrency];
                                          if (fromRate && toRate) {
                                            const converted = (amount / fromRate) * toRate;
                                            const displaySymbol = getCurrencySymbol(selectedCountry);
                                            return (
                                              <>
                                                <span className="text-blue-600 dark:text-blue-400">{nativeSymbol}{amount.toLocaleString()} {investmentCurrency}</span>
                                                <br />
                                                <span className="text-xs text-gray-500 dark:text-gray-400">(≈ {displaySymbol}{Math.round(converted).toLocaleString()})</span>
                                              </>
                                            );
                                          }
                                        } catch (e) {
                                          // Fallback
                                        }
                                      }

                                      // Fallback: show native currency
                                      return `${nativeSymbol}${amount.toLocaleString()} ${investmentCurrency}`;
                                    })()}
                                  </p>
                                  <div className="flex justify-between text-xs mt-1">
                                    <span className="text-green-600">{investment.return}% return</span>
                                    <span className={`font-medium ${
                                      investment.risk === 'High' ? 'text-red-600' :
                                      investment.risk === 'Medium' ? 'text-yellow-600' :
                                      'text-green-600'
                                    }`}>
                                      {investment.risk} risk
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}

                      {categoryGroups.length === 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
                          <Activity className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                          <p className="text-gray-600 dark:text-gray-400">No investments to display</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}


            {/* CSV Import Modal for Investments */}
            <CSVImportModal
              isOpen={showImportInvestmentsModal}
              title="Import Investments from CSV"
              type="investments"
              onClose={() => setShowImportInvestmentsModal(false)}
              onDownloadTemplate={handleDownloadInvestmentsTemplate}
              onValidate={(data) => validateInvestmentsCSV(data, accounts)}
              onImport={handleImportInvestmentsCSV}
              templateFilename="investments-import-template.csv"
              instructions={
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>CSV Format Requirements:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Required Headers:</strong> Investment Name, Ticker, Amount, Expected Return (%), Risk Level, Category, Account Name, Purchase Date</li>
                    <li><strong>Investment Name:</strong> Name of the investment (required)</li>
                    <li><strong>Ticker:</strong> Stock/crypto symbol like AAPL, BTC (optional)</li>
                    <li><strong>Amount:</strong> Positive number (e.g., 10000)</li>
                    <li><strong>Expected Return (%):</strong> Percentage as a number (e.g., 7 for 7%)</li>
                    <li><strong>Risk Level:</strong> Must be Low, Medium, or High</li>
                    <li><strong>Category:</strong> Stocks, Bonds, Real Estate, Commodities, Cryptocurrency, or Cash</li>
                    <li><strong>Account Name:</strong> Must match an existing account or will be marked as "Unassigned"</li>
                    <li><strong>Purchase Date:</strong> Format YYYY-MM-DD (optional, e.g., 2023-01-15)</li>
                    <li><strong>Tip:</strong> Download the template to see the correct format with examples</li>
                  </ul>
                </div>
              }
            />
          </div>
        )}

        {/* Retirement Tab - Enhanced Fintech-Grade Planner */}
        {activeTab === 'retirement' && (
          <RetirementPlanner
            accounts={accounts}
            investments={investments}
            country={selectedCountry}
            isPremium={userProfile?.plan === 'premium'}
          />
        )}

        {/* Budgeting Feature - Overview Tab */}
        {activeTab === 'budget-overview' && (
          <BudgetOverview
            country={selectedCountry}
            currency={currencySymbol}
            onNavigate={setActiveTab}
          />
        )}

        {/* Budgeting Feature - Monthly Budget Tab */}
        {activeTab === 'monthly-budget' && (
          <MonthlyBudget
            country={selectedCountry}
            currency={currencySymbol}
            onNavigate={setActiveTab}
          />
        )}

        {/* Budgeting Feature - Transactions Tab */}
        {activeTab === 'transactions' && (
          <Transactions
            country={selectedCountry}
            currency={currencySymbol}
            onNavigate={setActiveTab}
          />
        )}

        {/* Budgeting Feature - Trends Tab */}
        {activeTab === 'budget-trends' && (
          <Trends
            country={selectedCountry}
            currency={currencySymbol}
            onNavigate={setActiveTab}
          />
        )}

        {/* AI Insights Tab - Enhanced with Gemini Integration */}
        {activeTab === 'ai-analysis' && (
          <AIInsights
            investments={investments}
            accounts={accounts}
            cryptocurrencies={cryptocurrencies}
            portfolioData={portfolioData}
            retirementGoals={retirementGoals}
            aiAnalysis={aiAnalysis}
            analyzing={analyzing}
            runAIAnalysis={runAIAnalysis}
            onUpgrade={() => setShowUpgradeModal(true)}
            selectedCountry={selectedCountry}
          />
        )}

        {/* Net Worth Tab */}
        {activeTab === 'networth' && (
          <NetWorthDashboard
            isDark={isDark}
            investments={investments}
            retirementGoals={retirementGoals}
          />
        )}

        {/* Wealth Tracking Tab */}
        {activeTab === 'wealth-tracking' && (
          <WealthTrackingDashboard
            isDark={isDark}
            investments={investments}
            accounts={accounts}
            cryptoHoldings={cryptocurrencies}
            cashReserves={parseFloat(retirementGoals.currentSavings) || 0}
            exchangeRates={exchangeRates}
          />
        )}

        {/* Liabilities Tab */}
        {activeTab === 'liabilities' && (
          <LiabilitiesManager
            isDark={isDark}
          />
        )}

        {/* Financial Goals Tab */}
        {activeTab === 'goals' && (
          <FinancialGoals
            isDark={isDark}
            userProfile={userProfile}
            onUpgradeClick={() => setShowUpgradeModal(true)}
          />
        )}

        {/* Help & Learn Tab */}
        {activeTab === 'help-learn' && (
          <HelpAndLearn />
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <SupportForm
            onNavigateToTab={setActiveTab}
            onNavigateToLanding={onNavigateToLanding}
          />
        )}

        {/* Data Cleanup Tab */}
        {activeTab === 'data-cleanup' && (
          <DataCleanupPanel isDark={isDark} />
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <UserProfile
            onUpgrade={() => setShowUpgradeModal(true)}
            investments={investments}
            cryptocurrencies={cryptocurrencies}
            accounts={accounts}
          />
        )}
        </div>
      </div>

      {/* Add Investment Modal */}
      {showAddInvestment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add New Investment</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Investment Name</label>
                <input
                  type="text"
                  value={newInvestment.name}
                  onChange={(e) => setNewInvestment({...newInvestment, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Apple Stock"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticker Symbol <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newInvestment.ticker}
                  onChange={(e) => setNewInvestment({...newInvestment, ticker: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., AAPL, VWRP.L"
                />
                <p className="text-xs text-gray-500 mt-1">
                  For exchange-traded stocks/ETFs. Add .L suffix for London Stock Exchange.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ISIN <span className="text-gray-500 text-xs">(For UK funds without ticker)</span>
                </label>
                <input
                  type="text"
                  value={newInvestment.isin || ''}
                  onChange={(e) => setNewInvestment({...newInvestment, isin: e.target.value.toUpperCase()})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., GB00B0CNH163"
                  maxLength={12}
                />
                <p className="text-xs text-gray-500 mt-1">
                  For index funds, unit trusts, OEICs without exchange tickers (e.g., L&G, Vanguard UK funds)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={newInvestment.currency}
                  onChange={(e) => setNewInvestment({...newInvestment, currency: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="GBP">🇬🇧 GBP - British Pound</option>
                  <option value="EUR">🇪🇺 EUR - Euro</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select the currency this investment is denominated in
                </p>
              </div>

              {/* Quantity-based input section */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-blue-800 mb-2">Position Details (Recommended)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity <span className="text-gray-500 text-xs">({getUnitLabel(newInvestment.category)})</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={newInvestment.quantity}
                      onChange={(e) => setNewInvestment({...newInvestment, quantity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price/Unit ({newInvestment.priceInPence ? 'pence' : newInvestment.currency})
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={newInvestment.pricePerUnit}
                      onChange={(e) => setNewInvestment({...newInvestment, pricePerUnit: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={newInvestment.priceInPence ? "3251.42" : "32.51"}
                    />
                  </div>
                </div>
                {/* Pence toggle for GBP */}
                {newInvestment.currency === 'GBP' && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newInvestment.priceInPence}
                      onChange={(e) => setNewInvestment({...newInvestment, priceInPence: e.target.checked})}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Price is in pence (p)</span>
                    <span className="text-xs text-gray-500">(e.g., 3251.42p = £32.51)</span>
                  </label>
                )}
                {newInvestment.quantity && newInvestment.pricePerUnit && (
                  <p className="text-sm text-blue-700 font-medium">
                    {(() => {
                      const qty = parseFloat(newInvestment.quantity);
                      const rawPrice = parseFloat(newInvestment.pricePerUnit);
                      const priceInPounds = newInvestment.priceInPence ? rawPrice / 100 : rawPrice;
                      const total = qty * priceInPounds;
                      return `Total: ${newInvestment.currency === 'GBP' ? '£' : newInvestment.currency + ' '}${total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                    })()}
                  </p>
                )}
              </div>

              {/* OR divider */}
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-sm text-gray-500">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Legacy amount input (alternative) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount ({newInvestment.currency})
                  {!newInvestment.quantity && <span className="text-gray-500 text-xs ml-2">(Total invested)</span>}
                </label>
                <input
                  type="number"
                  value={newInvestment.amount}
                  onChange={(e) => setNewInvestment({...newInvestment, amount: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="15000"
                  disabled={newInvestment.quantity && newInvestment.pricePerUnit}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {newInvestment.quantity
                    ? `Cost basis: ${newInvestment.quantity} units × ${newInvestment.currency} ${newInvestment.pricePerUnit || 0} = ${newInvestment.currency} ${((newInvestment.quantity || 0) * (newInvestment.pricePerUnit || 0)).toFixed(2)}`
                    : `Enter the total amount invested in ${newInvestment.currency}`
                  }
                </p>
              </div>

              {/* Enhanced Tracking - Optional Quantity/Price Fields */}
              {(newInvestment.category === 'Stocks' || newInvestment.category === 'Bonds' || newInvestment.category === 'Cryptocurrency' || newInvestment.category === 'Commodities' || newInvestment.category === 'Real Estate' || newInvestment.category === 'Cash') && (newInvestment.ticker || newInvestment.isin) && (
                <div className="col-span-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start mb-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 mb-1">
                        Enable Live Pricing & Gain/Loss Tracking
                      </h4>
                      <p className="text-xs text-blue-700 mb-3">
                        Track quantity and purchase price to see current market value and gain/loss automatically.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quantity <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={newInvestment.quantity || ''}
                        onChange={(e) => setNewInvestment({...newInvestment, quantity: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Number of shares/units
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Purchase Price <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={newInvestment.purchasePrice || ''}
                        onChange={(e) => setNewInvestment({...newInvestment, purchasePrice: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 105.50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Price per unit when bought
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expected Return (%)</label>
                <input
                  type="number"
                  value={newInvestment.return}
                  onChange={(e) => setNewInvestment({...newInvestment, return: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="7"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newInvestment.category}
                  onChange={(e) => setNewInvestment({...newInvestment, category: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Stocks">Stocks</option>
                  <option value="Bonds">Bonds</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Commodities">Commodities</option>
                  <option value="Cryptocurrency">Cryptocurrency</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Held In Account <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <select
                  value={newInvestment.accountId}
                  onChange={(e) => {
                    const selectedAccount = accounts.find(acc => acc.id === e.target.value);
                    setNewInvestment({
                      ...newInvestment,
                      accountId: e.target.value,
                      accountName: selectedAccount ? selectedAccount.name : ''
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type || 'Account'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Link this investment to an account for better tracking
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                <select
                  value={newInvestment.risk}
                  onChange={(e) => setNewInvestment({...newInvestment, risk: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Date <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="date"
                  value={newInvestment.purchaseDate}
                  onChange={(e) => setNewInvestment({...newInvestment, purchaseDate: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => setShowAddInvestment(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddInvestment}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Add Investment
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Investment Modal */}
      {showEditInvestment && editingInvestment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Edit Investment</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Investment Name</label>
                <input
                  type="text"
                  value={editingInvestment.name}
                  onChange={(e) => setEditingInvestment({...editingInvestment, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Apple Stock"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ticker Symbol <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={editingInvestment.ticker || ''}
                  onChange={(e) => setEditingInvestment({...editingInvestment, ticker: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., AAPL, VWRP.L"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  For exchange-traded stocks/ETFs
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ISIN <span className="text-gray-500 text-xs">(For UK funds without ticker)</span>
                </label>
                <input
                  type="text"
                  value={editingInvestment.isin || ''}
                  onChange={(e) => setEditingInvestment({...editingInvestment, isin: e.target.value.toUpperCase()})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., GB00B0CNH163"
                  maxLength={12}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  For index funds, unit trusts, OEICs without exchange tickers
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Currency</label>
                <select
                  value={editingInvestment.currency || (selectedCountry === 'US' ? 'USD' : 'GBP')}
                  onChange={(e) => setEditingInvestment({...editingInvestment, currency: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="GBP">🇬🇧 GBP - British Pound</option>
                  <option value="EUR">🇪🇺 EUR - Euro</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Currency this investment is denominated in
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount ({editingInvestment.currency || currencySymbol})
                  {!editingInvestment.quantity && <span className="text-gray-500 text-xs ml-2">(Total invested)</span>}
                </label>
                <input
                  type="number"
                  value={editingInvestment.amount}
                  onChange={(e) => setEditingInvestment({...editingInvestment, amount: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10000"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {editingInvestment.quantity
                    ? `Cost basis: ${editingInvestment.quantity} units × ${editingInvestment.currency || currencySymbol} ${editingInvestment.purchasePrice || 0} = ${editingInvestment.currency || currencySymbol} ${((editingInvestment.quantity || 0) * (editingInvestment.purchasePrice || 0)).toFixed(2)}`
                    : `Enter the amount in ${editingInvestment.currency || currencySymbol}`
                  }
                </p>
              </div>

              {/* Enhanced Tracking - Optional Quantity/Price Fields */}
              {(editingInvestment.category === 'Stocks' || editingInvestment.category === 'Bonds' || editingInvestment.category === 'Cryptocurrency' || editingInvestment.category === 'Commodities' || editingInvestment.category === 'Real Estate' || editingInvestment.category === 'Cash') && (editingInvestment.ticker || editingInvestment.isin) && (
                <div className="col-span-2 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start mb-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                        Enable Live Pricing & Gain/Loss Tracking
                      </h4>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                        Track quantity and purchase price to see current market value and gain/loss automatically.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Quantity <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.000001"
                        value={editingInvestment.quantity || ''}
                        onChange={(e) => setEditingInvestment({...editingInvestment, quantity: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 50"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Number of shares/units
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Purchase Price <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={editingInvestment.purchasePrice || ''}
                        onChange={(e) => setEditingInvestment({...editingInvestment, purchasePrice: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g., 105.50"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Price per unit when bought
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expected Return (%)</label>
                <input
                  type="number"
                  value={editingInvestment.return}
                  onChange={(e) => setEditingInvestment({...editingInvestment, return: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="7"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                <select
                  value={editingInvestment.category}
                  onChange={(e) => setEditingInvestment({...editingInvestment, category: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Stocks">Stocks</option>
                  <option value="Bonds">Bonds</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Commodities">Commodities</option>
                  <option value="Cryptocurrency">Cryptocurrency</option>
                  <option value="Cash">Cash</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Held In Account <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <select
                  value={editingInvestment.accountId || ''}
                  onChange={(e) => {
                    const selectedAccount = accounts.find(acc => acc.id === e.target.value);
                    setEditingInvestment({
                      ...editingInvestment,
                      accountId: e.target.value,
                      accountName: selectedAccount ? selectedAccount.name : ''
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type || 'Account'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Link this investment to an account for better tracking
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Risk Level</label>
                <select
                  value={editingInvestment.risk}
                  onChange={(e) => setEditingInvestment({...editingInvestment, risk: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Purchase Date <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="date"
                  value={editingInvestment.purchaseDate || ''}
                  onChange={(e) => setEditingInvestment({...editingInvestment, purchaseDate: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowEditInvestment(false);
                  setEditingInvestment(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateInvestment}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Save Changes
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Position Modal */}
      {showAddToPosition && addToPositionTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto my-4">
            <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Add to Position</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Buy more {addToPositionTarget.name} ({addToPositionTarget.ticker || 'N/A'})
            </p>

            {/* Current Position Summary */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Position</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Quantity:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {addToPositionTarget.quantity ? formatQuantity(addToPositionTarget.quantity, addToPositionTarget.category) : '1'} {getUnitLabel(addToPositionTarget.category)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Avg Cost:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {addToPositionTarget.currency} {(addToPositionTarget.averageCostBasis || addToPositionTarget.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Total Cost Basis:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                    {addToPositionTarget.currency} {(addToPositionTarget.totalCostBasis || addToPositionTarget.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>
            </div>

            {/* New Purchase Input */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quantity ({getUnitLabel(addToPositionTarget.category)})
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={addToPositionData.quantity}
                    onChange={(e) => setAddToPositionData({...addToPositionData, quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price/Unit ({addToPositionData.priceInPence ? 'pence' : addToPositionTarget.currency})
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={addToPositionData.pricePerUnit}
                    onChange={(e) => setAddToPositionData({...addToPositionData, pricePerUnit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={addToPositionData.priceInPence ? "3251.42" : "32.51"}
                  />
                </div>
              </div>

              {/* Pence toggle for GBP */}
              {addToPositionTarget.currency === 'GBP' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addToPositionData.priceInPence}
                    onChange={(e) => setAddToPositionData({...addToPositionData, priceInPence: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Price is in pence (p)</span>
                  <span className="text-xs text-gray-500">(e.g., 3251.42p = £32.51)</span>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Purchase Date
                </label>
                <input
                  type="date"
                  value={addToPositionData.date}
                  onChange={(e) => setAddToPositionData({...addToPositionData, date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={addToPositionData.notes}
                  onChange={(e) => setAddToPositionData({...addToPositionData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Monthly DCA, Earnings dip buy"
                />
              </div>
            </div>

            {/* Preview of New Position */}
            {addToPositionData.quantity && addToPositionData.pricePerUnit && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-4">
                <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">After This Purchase</p>
                <div className="text-sm text-green-700 dark:text-green-400 space-y-1">
                  {(() => {
                    const newQty = parseFloat(addToPositionData.quantity);
                    const rawPrice = parseFloat(addToPositionData.pricePerUnit);
                    // Convert pence to pounds if needed
                    const newPrice = addToPositionData.priceInPence && addToPositionTarget.currency === 'GBP'
                      ? rawPrice / 100
                      : rawPrice;
                    const existingQty = addToPositionTarget.quantity || 1;
                    const existingCost = addToPositionTarget.totalCostBasis || addToPositionTarget.amount || 0;
                    const newPurchaseCost = newQty * newPrice;
                    const totalQty = existingQty + newQty;
                    const totalCost = existingCost + newPurchaseCost;
                    const newAvgCost = totalCost / totalQty;
                    const currencySymbol = addToPositionTarget.currency === 'GBP' ? '£' : (addToPositionTarget.currency === 'EUR' ? '€' : '$');

                    return (
                      <>
                        <p><span className="font-medium">New Total:</span> {formatQuantity(totalQty, addToPositionTarget.category)} {getUnitLabel(addToPositionTarget.category)}</p>
                        <p><span className="font-medium">New Avg Cost:</span> {currencySymbol}{newAvgCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 4})}</p>
                        <p><span className="font-medium">Total Cost Basis:</span> {currencySymbol}{totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowAddToPosition(false);
                  setAddToPositionTarget(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddToPosition}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all"
              >
                Add to Position
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade to Premium Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-4">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Upgrade to Premium</h2>
                <p className="text-gray-600 mb-4">Unlock AI-powered insights and advanced features</p>

                {/* Billing Cycle Toggle */}
                <div className="inline-flex items-center gap-3 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setUpgradeBillingCycle('monthly')}
                    className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                      upgradeBillingCycle === 'monthly'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setUpgradeBillingCycle('yearly')}
                    className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${
                      upgradeBillingCycle === 'yearly'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Yearly
                    <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                      Save {selectedCountry === 'US' ? '$7' : '£10'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Pricing Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Free Plan */}
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
                    <div className="text-3xl font-bold text-gray-900 mb-2">{currencySymbol}0</div>
                    <p className="text-sm text-gray-600">Your current plan</p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">Portfolio tracking & account management</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">Cryptocurrency tracking</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">Multi-country support (US & UK)</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">Financial goals tracking & projections</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Lock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-400 line-through">Net worth dashboard & health score</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Lock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-400 line-through">Liabilities & debt management</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Lock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-400 line-through">AI-powered analysis</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <Lock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-400 line-through">Advanced projections & backtesting</span>
                    </li>
                  </ul>
                </div>

                {/* Premium Plan */}
                <div className="border-2 border-purple-400 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                  
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium</h3>
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                      {upgradeBillingCycle === 'monthly'
                        ? (selectedCountry === 'US' ? '$5.99/mo' : '£4.99/mo')
                        : (selectedCountry === 'US' ? '$65/year' : '£49.99/year')
                      }
                    </div>
                    {upgradeBillingCycle === 'yearly' && (
                      <p className="text-sm text-green-600 font-semibold mb-1">
                        Save {selectedCountry === 'US' ? '$7' : '£10'} compared to monthly
                      </p>
                    )}
                    <p className="text-sm text-gray-600">14-day free trial • Cancel anytime</p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900 font-medium">Everything in Free, plus:</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Net worth dashboard with health score</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Liabilities & debt management</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Historical backtesting analysis</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Monte Carlo simulations</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">AI-powered insights & recommendations</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Risk assessment & diversification scores</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Advanced scenario planning</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Export to CSV & PDF</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-900">Priority support</span>
                    </li>
                  </ul>

                  <button
                    onClick={async () => {
                      setUpgrading(true);
                      try {
                        const country = selectedCountry; // 'US' or 'UK'
                        const email = currentUser.email;
                        const uid = currentUser.uid;

                        // Get Payment Link URL with selected billing cycle
                        const paymentUrl = getPaymentLinkUrl(country, upgradeBillingCycle, email, uid);

                        // Redirect to Stripe Payment Link
                        window.location.href = paymentUrl;
                      } catch (error) {
                        console.error('Error starting upgrade:', error);
                        alert('Failed to start upgrade. Please try again or contact support.');
                        setUpgrading(false);
                      }
                    }}
                    disabled={upgrading}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {upgrading ? 'Redirecting to checkout...' : 'Start 14-Day Free Trial'}
                  </button>
                  <p className="text-center text-xs text-gray-600 mt-3">
                    Start your trial today, then {upgradeBillingCycle === 'monthly' ? 'monthly billing' : 'billed yearly'}
                  </p>
                </div>
              </div>

              {/* Features Comparison */}
              <div className="mb-8 bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-center">Why Upgrade?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Brain className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Financial Health Check</h4>
                    <p className="text-sm text-gray-600">Comprehensive AI analysis of your portfolio, net worth, debt, goals, and priorities</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Target className="w-6 h-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Better Planning</h4>
                    <p className="text-sm text-gray-600">Advanced tools to optimize your financial future</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Zap className="w-6 h-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Priority Support</h4>
                    <p className="text-sm text-gray-600">Get help faster with priority email support</p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-semibold mb-1">Educational Tool Only</p>
                    <p>PlanLens provides educational insights and tools. This is not financial advice. Consult a qualified Independent Financial Advisor (IFA) for personalized guidance.</p>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="text-center">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-600 hover:text-gray-900 font-semibold"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestmentRetirementApp;
